import IconCalendar from '@assets/icons/calendar.svg?react';
import {
 Box,
 Button,
 FormControl,
 IconButton,
 InputAdornment,
 InputLabel,
 MenuItem,
 Popover,
 Select,
 TextField,
 Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
 DateRangeCalendar,
 DateRangePicker,
 LocalizationProvider,
 SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
 Control,
 Controller,
 UseFormSetValue,
 useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WidgetProgressProps } from './Dashboard.types';

dayjs.extend(weekOfYear);

interface DashboardWidgetModalDateProps {
 control: Control<WidgetProgressProps>;
 setValue: UseFormSetValue<WidgetProgressProps>;
 errors?: {
  dateFrom?: { message?: string };
  dateTo?: { message?: string };
 };
}

const DashboardWidgetModalDate = ({
 control,
 setValue,
 errors,
}: DashboardWidgetModalDateProps) => {
 const { t } = useTranslation();
 const th = useTheme();
 const [period, setPeriod] = useState<string>('');
 const [isCustomPopoverOpen, setIsCustomPopoverOpen] = useState(false);
 const datePickerAnchorRef = useRef<HTMLDivElement | null>(null);
 const [draftDateRange, setDraftDateRange] = useState<
  [Dayjs | null, Dayjs | null]
 >([null, null]);
 const [draftPeriod, setDraftPeriod] = useState<string>('');
 const [draftYear, setDraftYear] = useState<number | ''>('');
 const [draftMonth, setDraftMonth] = useState<number | ''>('');

 const dateFrom = useWatch({ control, name: 'dateFrom' });
 const dateTo = useWatch({ control, name: 'dateTo' });

 const now = useMemo(() => dayjs(), []);

 const periods = useMemo(
  () => [
   {
    value: [now.startOf('isoWeek'), now.endOf('isoWeek')],
    label: t('filterSidebar.thisWeek'),
   },
   {
    value: [
     now.subtract(7, 'day').startOf('isoWeek'),
     now.subtract(7, 'day').endOf('isoWeek'),
    ],
    label: t('filterSidebar.lastWeek'),
   },
   {
    value: [now.startOf('month'), now.endOf('month')],
    label: t('filterSidebar.thisMonth'),
   },
   {
    value: [
     now.subtract(1, 'month').startOf('month'),
     now.subtract(1, 'month').endOf('month'),
    ],
    label: t('filterSidebar.lastMonth'),
   },
   {
    value: [now.startOf('year'), now.endOf('year')],
    label: t('filterSidebar.thisYear'),
   },
   {
    value: [
     now.subtract(1, 'year').startOf('year'),
     now.subtract(1, 'year').endOf('year'),
    ],
    label: t('filterSidebar.lastYear'),
   },
  ],
  [now, t],
 );

 const formatDateToString = (date: Dayjs | null): string =>
  date ? date.format('YYYY-MM-DD') : '';

 const parseStringToDayjs = (dateString: string | undefined): Dayjs | null =>
  dateString ? dayjs(dateString) : null;

 const committedDateRange: [Dayjs | null, Dayjs | null] = [
  parseStringToDayjs(dateFrom),
  parseStringToDayjs(dateTo),
 ];

 const calculateDateRange = (
  type: 'year' | 'month',
  value: number,
  currentStart: Dayjs | null,
  currentEnd: Dayjs | null,
 ) => {
  let newStart: Dayjs;
  let newEnd: Dayjs;
  const fallbackStart = currentStart ?? dayjs();
  const fallbackEnd = currentEnd ?? fallbackStart;

  if (type === 'year') {
   if (value < 1900 || value > 2100) {
    return null;
   }
   newStart = fallbackStart.year(value).startOf('year');
   newEnd = fallbackEnd.year(value).endOf('year');
   return [newStart, newEnd] as [Dayjs, Dayjs];
  }

  if (value < 1 || value > 12) {
   return null;
  }

  newStart = fallbackStart.month(value - 1).startOf('month');
  newEnd = fallbackEnd.month(value - 1).endOf('month');
  return [newStart, newEnd] as [Dayjs, Dayjs];
 };

 const handleDateRangeInputChange = (
  newValue: [Dayjs | null, Dayjs | null],
 ) => {
  const [nextFrom, nextTo] = newValue;
  const isNextFromInvalid = nextFrom !== null && !dayjs(nextFrom).isValid();
  const isNextToInvalid = nextTo !== null && !dayjs(nextTo).isValid();

  if (isNextFromInvalid || isNextToInvalid) {
   return;
  }

  setValue('dateFrom', formatDateToString(nextFrom));
  setValue('dateTo', formatDateToString(nextTo));
  setPeriod('');
 };

 useEffect(() => {
  const checkPeriodMatch = (
   fromDate: string | undefined,
   toDate: string | undefined,
  ): string => {
   if (!fromDate || !toDate) {
    return '';
   }

   const from = parseStringToDayjs(fromDate);
   const to = parseStringToDayjs(toDate);

   if (!from || !to) {
    return '';
   }

   for (let i = 0; i < periods.length; i += 1) {
    const periodStart = periods[i].value[0];
    const periodEnd = periods[i].value[1];

    if (from.isSame(periodStart, 'day') && to.isSame(periodEnd, 'day')) {
     return String(i);
    }
   }

   return '';
  };

  const matchedPeriod = checkPeriodMatch(dateFrom, dateTo);
  setPeriod(matchedPeriod);
 }, [dateFrom, dateTo, periods]);

 const handleOpenCustomPopover = () => {
  setDraftDateRange([parseStringToDayjs(dateFrom), parseStringToDayjs(dateTo)]);
  setDraftPeriod(period);
  setDraftYear('');
  setDraftMonth('');
  setIsCustomPopoverOpen(true);
 };

 const handleCloseCustomPopover = () => {
  setIsCustomPopoverOpen(false);
 };

 const handleDraftPeriodChange = (value: string) => {
  setDraftPeriod(value);
  setDraftYear('');
  setDraftMonth('');

  if (value === '') {
   setDraftDateRange([null, null]);
   return;
  }

  const periodIndex = Number(value);
  const selectedPeriod = periods[periodIndex];
  if (!selectedPeriod) {
   return;
  }

  setDraftDateRange([selectedPeriod.value[0], selectedPeriod.value[1]]);
 };

 const handleDraftDatePartChange = (type: 'year' | 'month', value: number) => {
  const nextDateRange = calculateDateRange(
   type,
   value,
   draftDateRange[0],
   draftDateRange[1],
  );

  if (!nextDateRange) {
   return;
  }

  setDraftDateRange(nextDateRange);
  setDraftPeriod('');
 };

 const handleDraftCalendarChange = (newValue: [Dayjs | null, Dayjs | null]) => {
  setDraftDateRange(newValue);
  setDraftPeriod('');
 };

 const handleSubmitCustomPopover = () => {
  const [draftFrom, draftTo] = draftDateRange;
  setValue('dateFrom', formatDateToString(draftFrom));
  setValue('dateTo', formatDateToString(draftTo));
  setPeriod(draftPeriod);
  handleCloseCustomPopover();
 };

 return (
  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="lt">
   <Box>
    <Typography
     sx={{
      mb: '12px',
      fontSize: '14px',
      color: (theme) => theme.palette.kemek.darkGray,
      fontWeight: 500,
     }}
    >
     {t('dashboard.date')}
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
     <Controller
      name="dateFrom"
      control={control}
      render={() => (
       <DateRangePicker
        value={committedDateRange}
        onChange={handleDateRangeInputChange}
        ref={datePickerAnchorRef}
        open={false}
        slots={{ field: SingleInputDateRangeField }}
        slotProps={{
         textField: {
          fullWidth: true,
          label: t('dashboard.date'),
          error: !!errors?.dateFrom || !!errors?.dateTo,
          helperText: errors?.dateFrom?.message || errors?.dateTo?.message,
          InputProps: {
           endAdornment: (
            <InputAdornment position="end">
             <IconButton
              sx={{ width: 24, height: 24, mr: '-6px' }}
              title={t('global.selectDateRange')}
              aria-label={t('global.selectDateRange')}
              onClick={handleOpenCustomPopover}
              edge="end"
             >
              <IconCalendar
               style={{
                color: th.palette.kemek.gray,
               }}
              />
             </IconButton>
            </InputAdornment>
           ),
          },
         },
        }}
       />
      )}
     />
    </Box>
    <Popover
     open={isCustomPopoverOpen}
     anchorEl={datePickerAnchorRef.current}
     onClose={handleCloseCustomPopover}
     anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
     }}
     transformOrigin={{
      vertical: 'top',
      horizontal: 'left',
     }}
    >
     <Box
      sx={{
       display: 'flex',
       flexDirection: 'column',
       gap: 2,
       p: '12px',
       width: 520,
       minWidth: 520,
      }}
     >
      <Box sx={{ display: 'flex', gap: 1 }}>
       <FormControl sx={{ flex: 1 }}>
        <InputLabel id="custom-date-range-select-label">
         {t('filterSidebar.period')}
        </InputLabel>
        <Select
         value={draftPeriod}
         id="custom-date-range-select"
         labelId="custom-date-range-select-label"
         label={t('filterSidebar.period')}
         onChange={(event) => {
          const nextValue = event.target.value;
          handleDraftPeriodChange(nextValue);
         }}
         inputProps={{ 'aria-label': t('filterSidebar.period') }}
        >
         <MenuItem value="">{t('dashboard.empty')}</MenuItem>
         {periods.map((item, index) => (
          <MenuItem key={item.label} value={String(index)}>
           {item.label}
          </MenuItem>
         ))}
        </Select>
       </FormControl>
       <TextField
        value={draftYear}
        label={t('global.year')}
        type="number"
        onChange={(event) => {
         const nextValue = parseInt(event.target.value, 10);
         if (Number.isNaN(nextValue)) {
          setDraftYear('');
          return;
         }
         setDraftYear(nextValue);
         handleDraftDatePartChange('year', nextValue);
        }}
        sx={{ flex: 1, maxWidth: 115 }}
        slotProps={{
         htmlInput: {
          step: 1,
          min: 1900,
          max: 2100,
         },
        }}
       />
       <TextField
        value={draftMonth}
        label={t('global.month')}
        type="number"
        onChange={(event) => {
         const nextValue = parseInt(event.target.value, 10);
         if (Number.isNaN(nextValue)) {
          setDraftMonth('');
          return;
         }
         setDraftMonth(nextValue);
         handleDraftDatePartChange('month', nextValue);
        }}
        sx={{ flex: 1, maxWidth: 115 }}
        slotProps={{
         htmlInput: {
          step: 1,
          min: 1,
          max: 12,
         },
        }}
       />
      </Box>
      <DateRangeCalendar
       value={draftDateRange}
       onChange={handleDraftCalendarChange}
      />
      <Box
       sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        pt: '12px',
        borderTop: (theme) => `1px solid ${theme.palette.kemek.lightGray}`,
       }}
      >
       <Button
        variant="outlined"
        color="secondary"
        onClick={handleCloseCustomPopover}
       >
        {t('global.cancel')}
       </Button>
       <Button variant="contained" onClick={handleSubmitCustomPopover}>
        {t('global.apply')}
       </Button>
      </Box>
     </Box>
    </Popover>
   </Box>
  </LocalizationProvider>
 );
};

export default DashboardWidgetModalDate;

